'use client'

import { Fragment, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Laptop, Smartphone } from 'lucide-react'
import {
  Avatar,
  Badge,
  Button,
  EntityListShell,
  MultiSelect,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDate,
} from '@rr/ui'
import {
  usePlatformTenantUsersList,
  usePlatformUserSessions,
  useRevokePlatformSession,
} from '@/modules/platform/hooks/usePlatformUsers'
import { usePlatformCompaniesList } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 10

export default function UsersPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | 'active' | 'archived'>('all')
  const [organizationIds, setOrganizationIds] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
      status: status !== 'all' ? status : undefined,
      // @rr/api buildParams stringifies arrays as comma-joined; backend
      // splits on comma and applies WHERE companyId IN (...).
      organizationIds: organizationIds.length > 0 ? organizationIds : undefined,
    }),
    [page, search, status, organizationIds],
  )

  const { data, isLoading, isError, error } = usePlatformTenantUsersList(filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  // Organization filter options — pulled from platform organizations list.
  const orgsQuery = usePlatformCompaniesList({ page: 1, limit: 100 })
  const orgs: any[] = (orgsQuery.data as any)?.data?.items ?? []
  const orgOptions = useMemo(
    () => orgs.map((o: any) => ({ value: String(o.id), label: o.name ?? '—' })),
    [orgs],
  )

  return (
    <div className="page-pad">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          title="Users"
          subtitle="Tenant users across all organizations."
        />

        <EntityListShell
          toolbar={
            <>
              <SearchInput
                value={search}
                onChange={(v) => { setSearch(v); setPage(1) }}
                placeholder="Search name or email…"
                className="max-w-sm"
              />
              <div className="w-[280px]">
                <MultiSelect
                  options={orgOptions}
                  value={organizationIds}
                  onValueChange={(v) => { setOrganizationIds(v); setPage(1) }}
                  placeholder="All organizations"
                />
              </div>
              <Select
                value={status}
                onValueChange={(v) => { setStatus(v as typeof status); setPage(1) }}
              >
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </>
          }
          empty={!isLoading && items.length === 0}
          emptyState={
            isError
              ? error instanceof Error ? error.message : 'Failed to load users.'
              : 'No users match your filters.'
          }
          pagination={
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
          }
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><Spinner /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>User</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((u: any) => {
                  const id = String(u.id)
                  const name = u.name ?? u.email ?? '—'
                  const email = u.email ?? ''
                  const uStatus = (u.status ?? 'active') as 'active' | 'archived'
                  const expanded = expandedId === id
                  return (
                    <Fragment key={id}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() => setExpandedId(expanded ? null : id)}
                      >
                        <TableCell className="text-muted-foreground">
                          {expanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3 min-w-0">
                            <Avatar name={name} size={32} />
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate">{name}</p>
                              <p className="text-xs text-muted-foreground truncate">{email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {u.company?.name ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {u.department?.name ?? '—'}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={uStatus as any} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(u.createdAt)}
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={6} className="bg-muted/30 p-0">
                            <UserSessions userId={id} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </EntityListShell>
      </div>
    </div>
  )
}

/** "in 3 days" / "2 minutes ago" via platform Intl — no date lib needed. */
function relativeTime(iso: string): string {
  const diffSec = Math.round((new Date(iso).getTime() - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ]
  let value = diffSec
  for (const [amount, unit] of divisions) {
    if (Math.abs(value) < amount) return rtf.format(Math.round(value), unit)
    value /= amount
  }
  return rtf.format(Math.round(value), 'year')
}

/**
 * Lazy-loaded device-session list for one tenant user, rendered inside
 * the expanded row. Admins can force-revoke any device — the kicked
 * client 401s with `AUTH.SESSION_REVOKED` on its next request.
 */
function UserSessions({ userId }: { userId: string }) {
  const { data: sessions, isLoading, isError } = usePlatformUserSessions(userId, true)
  const revoke = useRevokePlatformSession(userId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner />
      </div>
    )
  }
  if (isError) {
    return (
      <p className="px-6 py-4 text-sm text-muted-foreground">
        Failed to load sessions.
      </p>
    )
  }
  if (!sessions || sessions.length === 0) {
    return (
      <p className="px-6 py-4 text-sm text-muted-foreground">
        No active sessions.
      </p>
    )
  }

  return (
    <div className="divide-y divide-border px-6">
      {sessions.map((s) => {
        const Icon = s.clientType === 'mobile' ? Smartphone : Laptop
        return (
          <div key={s.id} className="flex items-center justify-between gap-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">{s.label}</span>
                  <Badge variant="outline">{s.clientType}</Badge>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {s.ipAddress ? `${s.ipAddress} · ` : ''}
                  last seen {relativeTime(s.lastSeenAt)}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate(s.id)}
            >
              Revoke
            </Button>
          </div>
        )
      })}
    </div>
  )
}
